import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Chip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { LocalShippingOutlined, VisibilityOutlined } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

import CustomSnackBar from "../../Components/CustomSnackBar";
import MainText from "../../Components/MainText";
import { useAuth } from "../../context/AuthContext";
import { inventoryService } from "../../services/modules/inventory.service";
import ActiveGeneralInventoryAlert from "./components/ActiveGeneralInventoryAlert";

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.results)) return value.results;
  return [];
};

const toDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString();
};

function OutboundNotesPage() {
  const navigate = useNavigate();
  const { currentBranch, isSuperuser, effectivePerms = [] } = useAuth();
  const branchId = currentBranch?.id || null;

  const canView = Boolean(
    isSuperuser ||
      effectivePerms.includes("inventory_outbound_view")
  );
  const canCreate = Boolean(
    isSuperuser || effectivePerms.includes("inventory_outbound_create")
  );

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);

  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");
  const [snackSev, setSnackSev] = useState("info");

  const showSnack = (message, severity = "info") => {
    setSnackMsg(message);
    setSnackSev(severity);
    setSnackOpen(true);
  };

  const loadRows = useCallback(async () => {
    if (!branchId || !canView) {
      setRows([]);
      return;
    }
    setLoading(true);
    try {
      const data = await inventoryService.listOutboundNotes({
        branch_id: branchId,
        status: statusFilter || undefined,
      });
      setRows(toArray(data));
    } catch {
      setRows([]);
      showSnack("No se pudieron cargar las notas de salida.", "error");
    } finally {
      setLoading(false);
    }
  }, [branchId, canView, statusFilter]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const handleOpenDetail = useCallback(
    async (noteId) => {
      if (!branchId || !noteId) return;
      setDetailOpen(true);
      setDetailLoading(true);
      try {
        const data = await inventoryService.getOutboundNote(noteId, { branch_id: branchId });
        setSelectedNote(data || null);
      } catch {
        setSelectedNote(null);
        showSnack("No se pudo cargar el detalle de la nota.", "error");
      } finally {
        setDetailLoading(false);
      }
    },
    [branchId]
  );

  const columns = useMemo(
    () => [
      { field: "id", headerName: "ID", width: 90 },
      {
        field: "reference",
        headerName: "Referencia",
        minWidth: 180,
        valueGetter: (_, row) => row?.reference || "-",
      },
      {
        field: "reason",
        headerName: "Motivo",
        minWidth: 260,
        flex: 1,
        valueGetter: (_, row) => row?.reason || "-",
      },
      {
        field: "status",
        headerName: "Estado",
        width: 140,
        valueGetter: (_, row) => row?.status || "-",
      },
      {
        field: "origin",
        headerName: "Origen",
        width: 150,
        renderCell: ({ row }) =>
          row?.auto_generated ? <Chip size="small" color="info" label="Automatica" /> : <Chip size="small" variant="outlined" label="Manual" />,
      },
      {
        field: "items_count",
        headerName: "Items",
        width: 120,
        type: "number",
        valueGetter: (_, row) => toArray(row?.items).length,
      },
      {
        field: "created_at",
        headerName: "Fecha",
        width: 220,
        valueGetter: (_, row) => toDateTime(row?.created_at),
      },
      {
        field: "actions",
        headerName: "Acciones",
        width: 120,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={0.75}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<VisibilityOutlined fontSize="small" />}
              onClick={() => handleOpenDetail(row?.id)}
            >
              Ver
            </Button>
          </Stack>
        ),
      },
    ],
    [handleOpenDetail]
  );

  if (!canView) {
    return (
      <Box sx={{ py: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Sin permiso
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No tienes permisos para ver notas de salida.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <MainText title="Notas de salida" icon={LocalShippingOutlined} subtitle="Listado de salidas de inventario" />
        <Stack direction="row" spacing={1}>
          <TextField
            select
            size="small"
            label="Estado"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            sx={{ minWidth: 170 }}
          >
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="DRAFT">Borrador historico</MenuItem>
            <MenuItem value="APPROVED">Aplicada</MenuItem>
          </TextField>
          {canCreate && (
            <Button variant="contained" onClick={() => navigate("/inventory/outbound/create")}>
              Nueva nota
            </Button>
          )}
        </Stack>
      </Stack>

      <ActiveGeneralInventoryAlert />

      <Paper variant="outlined" sx={{ height: "calc(100vh - 280px)", minHeight: 360 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
          pageSizeOptions={[10, 20, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } } }}
        />
      </Paper>

      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Detalle de nota de salida</DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <Typography variant="body2" color="text.secondary">
              Cargando...
            </Typography>
          ) : !selectedNote ? (
            <Typography variant="body2" color="text.secondary">
              Sin detalle disponible.
            </Typography>
          ) : (
            <Stack spacing={1.25}>
              <Typography variant="body2">
                <strong>ID:</strong> {selectedNote.id}
              </Typography>
              <Typography variant="body2">
                <strong>Estado:</strong> {selectedNote.status}
              </Typography>
              <Typography variant="body2">
                <strong>Fecha:</strong> {toDateTime(selectedNote.created_at)}
              </Typography>
              <Typography variant="body2">
                <strong>Motivo:</strong> {selectedNote.reason || "-"}
              </Typography>

              <Paper variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Presentacion ID</TableCell>
                      <TableCell>Producto</TableCell>
                      <TableCell>Presentacion</TableCell>
                      <TableCell align="right">Cantidad</TableCell>
                      <TableCell align="right">Qty base</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {toArray(selectedNote.items).map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>{item.product_presentation_id}</TableCell>
                        <TableCell>{item.product_description || "-"}</TableCell>
                        <TableCell>{item.presentation_name || "-"}</TableCell>
                        <TableCell align="right">{item.qty_presentation}</TableCell>
                        <TableCell align="right">{item.qty_base}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <CustomSnackBar
        open={snackOpen}
        setOpen={setSnackOpen}
        message={snackMsg}
        severity={snackSev}
      />
    </Box>
  );
}

export default OutboundNotesPage;
